import { useState } from 'react';
import { Card } from '../Card';
import { StatusBadge } from './StatusBadge';
import { varinymAPI } from '../../lib/varinym-api';
import type { Invoice } from '../../types';

interface InvoiceHistoryCardProps {
  invoices: Invoice[];
  isLoading?: boolean;
}

export function InvoiceHistoryCard({ invoices, isLoading = false }: InvoiceHistoryCardProps) {
  const [loadingPdf, setLoadingPdf] = useState<string | null>(null);

  const handleDownloadPdf = async (pdfUrl: string, invoiceId: string) => {
    setLoadingPdf(invoiceId);
    try {
      const downloadUrl = await varinymAPI.getInvoicePdfUrl(pdfUrl);
      window.open(downloadUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Failed to download invoice PDF:', err);
    } finally {
      setLoadingPdf(null);
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatAmount = (amountStr: string, currency: string) => {
    const amount = parseFloat(amountStr);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount);
  };

  if (isLoading) {
    return (
      <Card title="Invoice History">
        <div className="text-center py-8 text-muted-foreground">Loading invoices...</div>
      </Card>
    );
  }

  if (!invoices || invoices.length === 0) {
    return (
      <Card title="Invoice History">
        <div className="text-center py-8 text-muted-foreground">
          <p>No invoices yet</p>
          <p className="text-xs mt-2">Invoices will appear here once you subscribe to a paid plan.</p>
        </div>
      </Card>
    );
  }

  return (
    <Card title="Invoice History">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-border">
          <thead>
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                Amount
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                Status
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {invoices.map((invoice) => (
              <tr key={invoice.id} className="hover:bg-accent/50">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                  {formatDate(invoice.billed_at)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-foreground">
                  {formatAmount(invoice.total, invoice.currency)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <StatusBadge type="invoice" value={invoice.status} />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                  {invoice.pdf_url ? (
                    <button
                      onClick={() => handleDownloadPdf(invoice.pdf_url!, invoice.id)}
                      disabled={loadingPdf === invoice.id}
                      className="text-primary hover:text-primary/80 transition-colors disabled:opacity-50"
                    >
                      {loadingPdf === invoice.id ? 'Loading...' : 'Download PDF'}
                    </button>
                  ) : invoice.receipt_url ? (
                    <a
                      href={invoice.receipt_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:text-primary/80 transition-colors"
                    >
                      View Receipt
                    </a>
                  ) : (
                    <span className="text-muted-foreground">N/A</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {invoices.length === 24 && (
        <div className="mt-4 text-xs text-muted-foreground text-center">
          Showing last 24 invoices
        </div>
      )}
    </Card>
  );
}
